import BaseDealerSelect from '@/vendor/Dealer/BaseDealerSelect.js';
import getCurrentLocation from '@/vendor/getCurrentLocation';

class MultistepQuotationDealerSelect extends BaseDealerSelect {
	mapReady = false;

	constructor(config) {
		super(Object.assign({
			scope: '[data-multistep-quotation-dealer-select="scope"]',
			dealer: '[data-multistep-quotation-dealer-select="dealer"]',
			locating: '[data-multistep-quotation-dealer-select="locating"]',
			zipcodeForm: '[data-multistep-quotation-dealer-select="zipcode-form"]',
			zipcodeInput: '[name="zipcode"]',
			zipcodeButton: '[data-multistep-quotation-dealer-select="set-zipcode"]',
			zipcodeError: '[data-multistep-quotation-dealer-select="zipcode-error"]',
			changeZipcode: '[data-multistep-quotation-dealer-select="change-zipcode"]',
			loader: '[data-multistep-quotation-dealer-select="loader"]',
			list: '[data-find-dealer="list"]',
			open: '[data-find-dealer="open"]',
			close: '[data-find-dealer="close"]',
			choices: '[data-find-dealer="choices"]',
			selectByMap: '[data-dealer-select]',
			selectByList: '[data-find-dealer="select-by-list"]',
			mapTab: '#nav-map-tab',
			locationTimeout: 10000,
			mapTimeout: 8000,
		}, config));
	}

	// De basisclass koppelt aan volledige adresvelden; hier is alleen de postcode van belang.
	addEventListeners() {
		this.$scope
			.on('click', this.config.open, this.open.bind(this))
			.on('click', this.config.close, this.close.bind(this))
			.on('submit', this.config.zipcodeForm, this.handleZipcodeSubmit.bind(this))
			.on('click', this.config.changeZipcode, this.handleChangeZipcode.bind(this))
			.on('click', this.config.selectByList, (event) => {
				event.preventDefault();
				this.setDealer(this.$scope.find(this.config.list).find('input[name="dealer"]:checked').val());
			})
			.on('click', this.config.selectByMap, (event) => {
				event.preventDefault();
				this.setDealer($(event.currentTarget).data('dealer-select'));
			})
			.on('click', this.config.mapTab, () => this.zoomMap());
	}

	getZipcode() {
		return (this.$scope.find(this.config.zipcodeInput).val() || '').trim();
	}

	// Alleen zonder bekende postcode de locatie opvragen; blijft de browser stil, dan verschijnt het postcodeveld alsnog.
	_getCurrentPosition() {
		if (this.getZipcode() !== '') {
			return Promise.resolve();
		}

		let timer = setTimeout(() => this.showZipcodeForm(), this.config.locationTimeout);

		return getCurrentLocation().then((data) => {
			clearTimeout(timer);

			if (this.getZipcode() !== '') {
				return;
			}

			if (data && data.zipcode) {
				this.$scope.find(this.config.zipcodeInput).val(data.zipcode);
				return this.setZipcode(data.zipcode);
			}

			this.showZipcodeForm();
		});
	}

	setLoading(loading) {
		this.$scope.find(this.config.loader).prop('hidden', !loading);
		this.$scope.find(this.config.zipcodeButton).prop('disabled', loading);
		this.$scope.find(this.config.dealer).attr('aria-busy', loading ? 'true' : null);
	}

	showZipcodeForm() {
		this.$scope.find(this.config.locating).prop('hidden', true);
		this.$scope.find(this.config.zipcodeForm).prop('hidden', false)
			.find(this.config.zipcodeInput).trigger('focus');
	}

	showZipcodeError(message) {
		this.showZipcodeForm();
		this.$scope.find(this.config.zipcodeError).text(message).prop('hidden', !message);
	}

	handleZipcodeSubmit(event) {
		event.preventDefault();
		this.setZipcode(this.getZipcode());
	}

	handleChangeZipcode(event) {
		event.preventDefault();
		this.showZipcodeForm();
	}

	async setZipcode(zipcode) {
		this.setLoading(true);

		try {
			const response = await $.ajax({
				url: this.$scope.data('route'),
				method: 'POST',
				data: {action: 'set_zipcode', zipcode: zipcode},
			});

			this.onDealerChanged(response);

			if (this.initialised) {
				await this.search();
			}
		} catch (xhr) {
			this.showZipcodeError(xhr.responseJSON?.errors?.zipcode?.[0] || xhr.responseJSON?.message || 'Er is iets fout gegaan.');
		} finally {
			this.setLoading(false);
		}
	}

	async setDealer(id) {
		if (!id) {
			return;
		}

		try {
			const response = await $.ajax({
				url: this.$scope.data('route'),
				method: 'POST',
				data: {action: 'select_dealer', dealer_id: id},
			});

			this.onDealerChanged(response);
			this.close();
		} catch (xhr) {
			alert(xhr.responseJSON?.errors?.dealer_id?.[0] || xhr.responseJSON?.message || 'Er is iets fout gegaan.');
		}
	}

	onDealerChanged(response) {
		if (response.dealer !== undefined) {
			this.$scope.find(this.config.dealer).html(response.dealer);
		}
	}

	// De lijst en kaart komen uit de offertetool zelf (sessie-postcode), niet uit de dealer-module.
	async search() {
		const response = await $.ajax({
			url: this.$scope.data('route'),
			method: 'POST',
			data: {action: 'get_dealers', range: this.$scope.data('range')},
		});

		this.$scope.find(this.config.list).html(response.list);

		if (this.mapReady) {
			this.map.setMarkers(response.markers);
		}

		return true;
	}

	async open(event) {
		event?.preventDefault();

		if (this.getZipcode() === '') {
			this.showZipcodeForm();
			return;
		}

		// Eerst tonen, anders krijgt de kaart geen afmetingen bij het initialiseren.
		this.showUI();

		if (!this.initialised) {
			this.initialised = true;
			await this.initMap();
			await this.search();
		}

		this.zoomMap();
	}

	// Zonder (geldige) Google-key blijft de lijst bruikbaar; de kaart wordt dan overgeslagen.
	async initMap() {
		let timeout = new Promise((resolve, reject) => setTimeout(() => reject(new Error('Google Maps laadt niet.')), this.config.mapTimeout));

		try {
			await Promise.race([this.map.init(), timeout]);
			this.mapReady = true;
		} catch (error) {
			console.warn(error);
		}
	}

	zoomMap() {
		if (this.mapReady && this.map.bounds && !this.map.bounds.isEmpty()) {
			this.map.zoom();
		}
	}

	showUI() {
		this.$scope.find(this.config.choices).prop('hidden', false);
	}

	close() {
		this.$scope.find(this.config.choices).prop('hidden', true);
	}
}

export default MultistepQuotationDealerSelect;
